<template>
  <div class="coupon-bind-page">
    <h2>绑定礼券</h2>
    <p class="lead">
      输入礼券号。绑定成功后按券类型发放礼遇。每位用户终身仅可绑定一张；臻享伙伴不能绑臻享券，普通人不能绑自己的双享券。
    </p>
    <p v-if="bonusHint" class="bonus-hint">{{ bonusHint }}</p>

    <el-skeleton v-if="loading && isLoggedIn" rows="3" animated />

    <template v-else>
    <el-alert
      v-if="!isLoggedIn"
      type="info"
      :closable="false"
      show-icon
      title="请先登录"
      description="绑定礼券需要登录账号。注册成功后将引导您到此页填写礼券号。"
      class="block-alert"
    >
      <el-button type="primary" size="small" @click="goLogin">去登录</el-button>
    </el-alert>

    <el-card v-else-if="bindStatus?.hasBinding" shadow="never" class="status-card">
      <h3>已绑定礼券</h3>
      <el-descriptions :column="1" border size="small">
        <el-descriptions-item label="券号">{{ bindStatus.couponNo }}</el-descriptions-item>
        <el-descriptions-item label="类型">
          {{ couponTypeLabel(bindStatus.couponType) }}
        </el-descriptions-item>
        <el-descriptions-item v-if="bindStatus.referrerNickname" label="推荐人">
          {{ bindStatus.referrerNickname }}
        </el-descriptions-item>
      </el-descriptions>
      <div class="actions">
        <el-button @click="$router.push('/referral/earnings')">查看推荐收益</el-button>
        <el-button @click="$router.push('/referral/coupons')">我的礼券</el-button>
      </div>
    </el-card>

    <el-card v-else-if="bindStatus && !bindStatus.canBind" shadow="never" class="bind-card">
      <el-alert type="warning" :closable="false" show-icon title="暂不可绑定">
        {{ bindStatus.bindBlockedReason || '当前无法绑定礼券，请联系客服' }}
      </el-alert>
    </el-card>

    <el-card v-else shadow="never" class="bind-card">
      <div v-if="showSkipBar" class="skip-bar">
        <span class="skip-bar__text">暂无礼券？可稍后在「我的 → 绑定礼券」中补绑。</span>
        <div class="skip-bar__actions">
          <el-checkbox v-model="skipReminder">不再提醒</el-checkbox>
          <el-button @click="handleSkip">跳过</el-button>
        </div>
      </div>
      <h3>输入券号</h3>
      <p class="hint">登录后随时可绑，终身仅可绑定一张礼券。</p>
      <div class="bind-row">
        <el-input
          v-model="couponNo"
          placeholder="例如 RC1A2B3C4D5E"
          maxlength="32"
          clearable
          @blur="onCheck"
          @keyup.enter="submit"
        />
        <el-button type="primary" :loading="binding" @click="submit">绑定</el-button>
      </div>
      <p v-if="checkHint" class="check-hint" :class="{ error: checkError }">{{ checkHint }}</p>
    </el-card>
    </template>
  </div>
</template>

<script>
import { goToLogin, isLoggedIn } from '@/utils/loginFlow'
import {
  bindReferralCoupon,
  buildBindBonusHint,
  checkReferralCoupon,
  fetchReferralBindStatus,
  fetchReferralSettings,
  COUPON_TYPE_LABELS,
  couponTypeLabel
} from '@/utils/referralCoupon'
import {
  isReferralBindReminderSkipped,
  notifyReferralBindStatusChanged,
  setReferralBindReminderSkipped
} from '@/utils/referralBindReminder'

export default {
  name: 'ReferralCouponBindView',
  data() {
    return {
      couponNo: '',
      checkHint: '',
      checkError: false,
      binding: false,
      bindStatus: null,
      loading: true,
      bonusHint: '',
      referralSettings: null,
      skipReminder: false
    }
  },
  computed: {
    isLoggedIn() {
      return isLoggedIn()
    },
    showSkipBar() {
      return this.isLoggedIn && this.bindStatus && !this.bindStatus.hasBinding && this.bindStatus.canBind
    },
    skipTarget() {
      const href = this.$route.query.href
      if (typeof href === 'string' && href.startsWith('/')) return href
      return '/'
    }
  },
  created() {
    const q = this.$route.query.coupon || this.$route.query.couponNo
    if (typeof q === 'string' && q.trim()) {
      this.couponNo = q.trim()
    }
    this.skipReminder = isReferralBindReminderSkipped()
    this.initPage()
  },
  methods: {
    couponTypeLabel,
    goLogin() {
      goToLogin(this.$router, this.$route.fullPath)
    },
    handleSkip() {
      if (this.skipReminder) {
        setReferralBindReminderSkipped(true)
      } else {
        notifyReferralBindStatusChanged()
      }
      this.$router.push(this.skipTarget)
    },
    async initPage() {
      try {
        this.referralSettings = await fetchReferralSettings()
      } catch {
        // 礼遇提示使用默认配置
      }
      if (!this.isLoggedIn) {
        this.loading = false
        if (this.couponNo) {
          await this.onCheck()
        }
        return
      }
      try {
        this.bindStatus = await fetchReferralBindStatus()
        if (!this.bindStatus.hasBinding && this.couponNo) {
          await this.onCheck()
        }
      } catch (e) {
        this.$message.error(e.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    async onCheck() {
      if (!this.couponNo.trim()) {
        this.checkHint = ''
        this.checkError = false
        return
      }
      try {
        const data = await checkReferralCoupon(this.couponNo)
        if (data.valid) {
          const typeLabel = COUPON_TYPE_LABELS[data.couponType] || ''
          this.checkHint = data.message || `「${data.displayName || ''}」可绑定${typeLabel ? ` · ${typeLabel}` : ''}`
          this.checkError = false
          this.bonusHint = buildBindBonusHint(data.couponType, this.referralSettings)
        } else {
          this.bonusHint = ''
          this.checkHint = data.message || '礼券无效'
          this.checkError = true
        }
      } catch {
        this.checkHint = ''
        this.checkError = false
      }
    },
    async submit() {
      if (!this.isLoggedIn) {
        this.goLogin()
        return
      }
      this.binding = true
      try {
        const res = await bindReferralCoupon(this.couponNo)
        this.$message.success(res.message || '绑定成功')
        this.bindStatus = await fetchReferralBindStatus()
        notifyReferralBindStatusChanged()
        this.couponNo = ''
        this.checkHint = ''
        const href = this.$route.query.href
        if (typeof href === 'string' && href.startsWith('/')) {
          this.$router.push(href)
        }
      } catch (e) {
        this.$message.error(e.message || '绑定失败')
        await this.onCheck()
      } finally {
        this.binding = false
      }
    }
  }
}
</script>

<style scoped>
.coupon-bind-page {
  max-width: 560px;
  margin: 0 auto;
  padding: 24px 16px 48px;
}
h2 {
  margin: 0 0 8px;
}
.lead {
  color: #606266;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0 0 12px;
}
.bonus-hint {
  color: #409eff;
  font-size: 13px;
  margin: 0 0 16px;
}
.block-alert {
  margin-bottom: 20px;
}
.status-card h3,
.bind-card h3 {
  margin: 0 0 12px;
  font-size: 1.05rem;
}
.hint {
  color: #909399;
  font-size: 13px;
  margin: 0 0 16px;
}
.skip-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}
.skip-bar__text {
  flex: 1;
  min-width: 200px;
  font-size: 13px;
  color: #909399;
  line-height: 1.5;
}
.skip-bar__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.bind-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.bind-row .el-input {
  flex: 1;
  min-width: 200px;
}
.check-hint {
  margin: 12px 0 0;
  font-size: 13px;
  color: #67c23a;
}
.check-hint.error {
  color: #f56c6c;
}
.actions {
  margin-top: 16px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
</style>
